import {CONFIG} from "../../../util/config";
import ReCAPTCHA from "react-google-recaptcha";
import React, {forwardRef, useCallback, useEffect, useImperativeHandle, useRef} from "react";
import {useLocation} from "react-router-dom";

export interface CaptchaCallableFunctions {
    /**
     * Reset the captcha widget.
     *
     * Typically, the captcha token can only be used once.
     * After an unsuccessful login attempt, this method should be called
     * to clear the current captcha and allow the user to solve a new one.
     */
    reset: () => void
}

/**
 * Captcha component
 *
 * Enables easy swapping of captcha providers without affecting other components.
 *
 * Use `reset()` to clear the captcha after failed validation.
 */
const XCAPTCHA = "xcaptcha";
const XCAPTCHA_AT = "xcaptch_at";

const Captcha = forwardRef<CaptchaCallableFunctions, { onChange: (token: string | null, captchaAt: number) => void }>(
    ({ onChange }, ref) => {

        const useQuery = () => {
            return new URLSearchParams(useLocation().search);
        }

        const toNumber = (numberAsString: string | null) => {
            if (numberAsString === null) {
                return null;
            }
            const num = Number(numberAsString);
            return isNaN(num) ? null : num;
        }
        const xcaptcha = useQuery().get(XCAPTCHA);
        const xcaptchaAt = toNumber(useQuery().get(XCAPTCHA_AT));

        const recaptchaRef = useRef<ReCAPTCHA>(null);

        const captchaDisabled = (() => {
            if (xcaptcha !== null && xcaptchaAt !== null) {
               return true;
            }
            else if (process.env.REACT_APP_CAPTCHA_DISABLE === "true") {
                return true
            }
        })();

        const checkIfCaptchaDisabled = useCallback(() => {
            if (xcaptcha !== null && xcaptchaAt !== null) {
                onChange(xcaptcha, xcaptchaAt)
            }
            else if (captchaDisabled) {
                onChange("false-token", new Date().getTime())
            }
            // eslint-disable-next-line
        }, [])

        useImperativeHandle(ref, () => ({
            reset: () => {
                if (captchaDisabled) {
                    checkIfCaptchaDisabled()
                }
                recaptchaRef.current?.reset();
            }
        }));

        useEffect(() => {
            checkIfCaptchaDisabled()
        }, [checkIfCaptchaDisabled]);

        if (captchaDisabled) {
            return <></>
        }

        return (
            <ReCAPTCHA
                sitekey={CONFIG.recaptchaSiteKey}
                onChange={(token) => onChange(token, new Date().getTime())}
                ref={recaptchaRef}
            />
        );
    }
);

export default Captcha;